import { z } from "zod";

import { MAX_PROMPT_LENGTH, VersionTypeSchema } from "./agent-version.schemas";

// ============================================================================
// PROMPT VARIANTS — branch-based prompt versioning (Prompt Lab phase 1)
//
// A variant is a prompt branch. Every agent has exactly one Main variant (the
// production lineage); named variants fork from any version and iterate
// independently. There is no merge and no rebase: a variant reaches Main only
// through promote, which appends a NEW Main version. These schemas are the
// single source for both the public v1 contract and the internal
// `ZPromptVariants` domain contract (same shapes, per spec §4.4).
// ============================================================================

export const PromptVariantStatusSchema = z.enum(["ACTIVE", "ARCHIVED"]);

export const PromptVariantSchema = z.object({
  id: z.string().uuid(),
  agentId: z.string().uuid(),
  name: z.string(),
  isMain: z.boolean(),
  status: PromptVariantStatusSchema,
  /// Graph edge: the version this variant branched off. Null for Main.
  forkedFromVersionId: z.string().uuid().nullable(),
  /// How many versions sit on this variant. The tip is the highest ordinal.
  versionCount: z.number().int(),
  /// Id of the tip version. Null only for a Main that has never saved.
  tipVersionId: z.string().uuid().nullable(),
  createdById: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

/// A version as seen through its variant: ordinal position, promote provenance.
export const PromptVariantVersionSchema = z.object({
  id: z.string().uuid(),
  variantId: z.string().uuid(),
  variantName: z.string(),
  /// 1-based position within the variant; the tip is the highest ordinal.
  ordinal: z.number().int(),
  type: VersionTypeSchema,
  name: z.string().nullable(),
  /// Set only on Main versions created by a promote: the variant-tip version
  /// whose content this Main version copied.
  promotedFromVersionId: z.string().uuid().nullable(),
  isProduction: z.boolean(),
  createdAt: z.coerce.date(),
  createdBy: z.string().nullable()
});

// ---------------------------------------------------------------------------
// Path params — variant routes address a variant by REF: its uuid, its name,
// or "main" (case-insensitive). Not `.uuid()` on purpose: names are legal.
// ---------------------------------------------------------------------------

export const AgentIdAndVariantRefParamSchema = z.object({
  agentId: z.string().uuid(),
  variantRef: z.string().min(1)
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

/// Query strings arrive as strings; "true" is the only value that includes
/// archived variants. `z.coerce.boolean()` would read "false" as true.
export const ListPromptVariantsParamsSchema = z.object({
  includeArchived: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional()
});

export const ListPromptVariantsResponseSchema = z.array(PromptVariantSchema);

// ---------------------------------------------------------------------------
// Create / fork / rename / archive
// ---------------------------------------------------------------------------

export const CreatePromptVariantBodySchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    /// Version to fork from. Defaults to the Main tip. NOT `.uuid()`: an unknown
    /// ref must answer "not found", not a format error.
    fromVersionId: z.string().min(1).optional()
  })
  .strict();

export const CreatePromptVariantResponseSchema = PromptVariantSchema;

export const ForkPromptVariantBodySchema = z
  .object({
    name: z.string().trim().min(1).max(80)
  })
  .strict();

export const ForkPromptVariantResponseSchema = PromptVariantSchema;

export const RenamePromptVariantBodySchema = z
  .object({
    name: z.string().trim().min(1).max(80)
  })
  .strict();

export const RenamePromptVariantResponseSchema = PromptVariantSchema;

export const ArchivePromptVariantResponseSchema = PromptVariantSchema;

// ---------------------------------------------------------------------------
// Save (append a version to a variant) + history
// ---------------------------------------------------------------------------

export const SavePromptVariantVersionBodySchema = z
  .object({
    prompt: z.string().max(MAX_PROMPT_LENGTH),
    name: z.string().trim().min(1).max(100).optional(),
    description: z.string().max(500).optional()
  })
  .strict();

export const SavePromptVariantVersionResponseSchema = PromptVariantVersionSchema;

/// Ascending by ordinal — `[-1]` is the tip.
export const ListPromptVariantVersionsResponseSchema = z.array(PromptVariantVersionSchema);

// ---------------------------------------------------------------------------
// Promote
// ---------------------------------------------------------------------------

export const PromotePromptVariantBodySchema = z
  .object({
    /// When true, the new Main version also becomes the production version
    /// (same effect as the existing versions publish flow).
    publish: z.boolean().optional()
  })
  .strict();

export const PromotePromptVariantResponseSchema = z.object({
  /// The NEW version appended to Main — history is never rewritten.
  newMainVersionId: z.string().uuid(),
  mainVariantId: z.string().uuid(),
  ordinal: z.number().int(),
  /// The variant tip the content was copied from (= promotedFromVersionId).
  sourceVersionId: z.string().uuid(),
  published: z.boolean()
});

// ---------------------------------------------------------------------------
// Graph
// ---------------------------------------------------------------------------

export const PromptGraphNodeSchema = z.object({
  id: z.string().uuid(),
  variantId: z.string().uuid(),
  variantName: z.string(),
  isMain: z.boolean(),
  ordinal: z.number().int(),
  type: VersionTypeSchema,
  name: z.string().nullable(),
  promotedFromVersionId: z.string().uuid().nullable(),
  isProduction: z.boolean(),
  createdAt: z.coerce.date()
});

export const PromptGraphEdgeSchema = z.object({
  /// fork: from = the version forked off, to = the variant's first version.
  /// promote: from = the variant tip, to = the new Main version.
  kind: z.enum(["fork", "promote"]),
  from: z.string().uuid(),
  to: z.string().uuid()
});

export const PromptGraphResponseSchema = z.object({
  nodes: z.array(PromptGraphNodeSchema),
  edges: z.array(PromptGraphEdgeSchema)
});

// ---------------------------------------------------------------------------
// Compare
// ---------------------------------------------------------------------------

/// A ref is a version id, a variant name (meaning its tip), or "main".
export const ComparePromptParamsSchema = z.object({
  a: z.string().min(1),
  b: z.string().min(1)
});

export const ComparePromptRefSchema = z.object({
  ref: z.string(),
  versionId: z.string().uuid(),
  variantName: z.string(),
  ordinal: z.number().int()
});

/// One line-level difference. `line` is 1-based in the side the op names:
/// `remove` counts lines of a, `add` counts lines of b.
export const ComparePromptChangeSchema = z.object({
  op: z.enum(["add", "remove"]),
  line: z.number().int(),
  text: z.string()
});

export const ComparePromptResponseSchema = z.object({
  a: ComparePromptRefSchema,
  b: ComparePromptRefSchema,
  /// Empty when the two refs hold identical prompt text.
  changes: z.array(ComparePromptChangeSchema)
});
